import React, { useState, Fragment } from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";

import LinkWrapper from "@/components/linkWrapper";
import MediaWrapper from "@/components/mediaWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CheckIcon,
} from "@heroicons/react/24/solid";
import { Listbox, Transition } from "@headlessui/react";
import { useInView } from "react-intersection-observer";

/*
  This component requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ],
  }
  ```
*/

export const query = graphql`
  fragment BlogThreeColumnCardsWithFilterArticle on StrapiArticle {
    id
    title
    locale
    url
    date
    categories {
      url
      title
    }
    author {
      url
      name
      image {
        ...StrapiMediaSubquery
      }
    }
    readingTimeMinutes
    description
    descriptionShort
    headImage {
      ...StrapiMediaSubquery
    }
  }

  fragment BlogThreeColumnCardsWithFilterCategory on StrapiCategory {
    id
    url
    title
    articles {
      id
    }
  }

  fragment BlogThreeColumnCardsWithFilterData on StrapiComponentBlogThreeColumnCardsWithFilter {
    topic
    title
    description
    readingTimeText
    categoryPlaceholder
    buttonTextMore
    searchPlaceholder
    searchButtonText
    searchButtonIcon {
      ...StrapiMediaSubquery
    }
    noPostsFoundText
    noPostsFoundImage {
      ...StrapiMediaSubquery
    }
    paginationPrevious
    paginationNext
    paginationCurrentPageText
  }
`;

const articlePerPage = 6;
const descriptionMaxWordCount = 50;

function limitTextByWordCount(text, wordMaxCount) {
  let wordSplits = text.split(" ");
  let wordCount = wordSplits?.length;
  if (wordCount > wordMaxCount) {
    wordSplits = wordSplits.slice(0, wordMaxCount);
    return wordSplits.join(" ") + "...";
  } else {
    return text;
  }
}

function ThreeColumnCardsWithFilter({
  data,
  blogData,
  articles = [],
  categories = [],
}) {
  if (!data || !blogData) {
    return <></>;
  }

  const allCategories = categories.sort(
    (a, b) => (b.articles?.length || 0) - (a.articles?.length || 0)
  );
  const activeCategories = blogData.activeCategoryIds
    .map((categoryId) =>
      allCategories.find((category) => category.id === categoryId)
    )
    .filter(Boolean);
  allCategories?.map((category) => {
    const { ref, inView } = useInView({
      initialInView: true,
      threshold: 1,
    });
    let isActive = activeCategories?.find(
      (activeCategory) => activeCategory.id === category.id
    )
      ? true
      : false;
    Object.assign(category, { isActive, observer: { ref, inView } });
  });
  const inactiveCategories = allCategories?.filter(
    (category) => !category.isActive
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilter, setSearchFilter] = useState("");
  const [activeSearchFilter, setActiveSearchFilter] = useState("");

  let totalPages = 0;

  function increasePage() {
    goToPage(currentPage + 1);
  }

  function decreasePage() {
    goToPage(currentPage - 1);
  }

  function goToPage(page) {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      document.getElementById("divFirst").scrollIntoView();
    }
  }

  function isPageActive(i) {
    return currentPage === i;
  }

  function isFirstPage() {
    return currentPage === 1;
  }

  function isLastPage() {
    return currentPage === totalPages;
  }

  function applySearchFilter(e) {
    e.preventDefault();
    setActiveSearchFilter(searchFilter);
    setCurrentPage(1);
  }

  let posts = [...articles];

  if (activeSearchFilter !== "") {
    posts = posts.filter((post) =>
      JSON.stringify([
        post.title || "",
        post.description || "",
        post.descriptionShort || "",
      ])
        .toLowerCase()
        .includes(activeSearchFilter.trim().toLowerCase())
    );
  }

  totalPages = Math.ceil(posts.length / articlePerPage);

  let pageLinks = [];
  for (let i = 1; i <= totalPages; i++) {
    pageLinks.push(i);
  }
  let pageLinksLeft = null;
  let pageLinksMiddle = pageLinks;
  let pageLinksRight = null;
  if (totalPages >= 9) {
    if (currentPage > 4) pageLinksLeft = [1];

    let pageLinksMiddleStart =
      currentPage <= 4
        ? 0
        : currentPage >= totalPages - 3
          ? totalPages - 5
          : currentPage - 2;
    let pageLinksMiddleEnd =
      currentPage <= 4
        ? 5
        : currentPage >= totalPages - 3
          ? totalPages
          : currentPage + 1;
    pageLinksMiddle = pageLinks.slice(pageLinksMiddleStart, pageLinksMiddleEnd);

    if (currentPage < totalPages - 3) pageLinksRight = [totalPages];
  }
  posts = posts.slice(
    articlePerPage * (currentPage - 1),
    articlePerPage * (currentPage - 1) + articlePerPage
  );

  return (
    <div className="mx-auto max-w-7xl bg-background-white px-4 pb-20 pt-16 lg:px-6 lg:pb-28 lg:pt-24">
      <div id="divFirst" className="text-center">
        {data.topic && (
          <h3 className="block text-center text-base font-semibold uppercase tracking-wide text-text-primary">
            <MarkdownWrapper text={data.topic} />
          </h3>
        )}
        {data.title && (
          <h2 className="text-3xl font-extrabold tracking-tight text-text-primary sm:text-4xl">
            <MarkdownWrapper text={data.title} />
          </h2>
        )}
        {data.description && (
          <MarkdownWrapper
            text={data.description}
            markdownClassName="mt-3 max-w-2xl mx-auto text-xl text-text-secondary sm:mt-4"
            replaceParagraph={false}
          />
        )}
      </div>

      <div className="mt-8">
        <div className="md:hidden">
          <Listbox value={null} onChange={() => {}}>
            {({ open }) => (
              <>
                {data.categoryPlaceholder && (
                  <Listbox.Label className="sr-only">
                    {data.categoryPlaceholder}
                  </Listbox.Label>
                )}
                <div className="relative">
                  <Listbox.Button
                    className={`relative w-full rounded-md border border-border-gray py-2 pl-3 pr-10 text-left text-base text-text-gray hover:border-border-dark-gray hover:text-text-dark-gray focus:border-border-primary focus:outline-none focus:ring-ring-primary sm:text-sm ${
                      open || activeCategories.length
                        ? "border-border-dark-gray text-text-dark-gray"
                        : ""
                    }`}
                  >
                    <span
                      className={`relative ${
                        activeCategories.length ? "text-text-primary" : ""
                      }`}
                    >
                      {activeCategories.length
                        ? activeCategories[0].title
                        : data.categoryPlaceholder
                          ? data.categoryPlaceholder
                          : "Category"}
                    </span>
                    <span className="absolute right-2 items-center">
                      <ChevronDownIcon className="h-5 w-5" />
                    </span>
                  </Listbox.Button>

                  <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-background-white py-1 text-base shadow-lg ring-1 ring-ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {Boolean(allCategories?.length) &&
                        allCategories.map((category, index) => (
                          <Listbox.Option
                            key={index}
                            className="relative cursor-default select-none px-4 py-2 text-text-gray"
                          >
                            <LinkWrapper
                              to={
                                category.isActive
                                  ? blogData.rootUrl
                                  : category.url
                              }
                              className={`relative flex truncate pl-6 font-normal hover:text-text-dark-gray ${
                                category.isActive ? "text-text-dark-gray" : ""
                              }`}
                            >
                              {category.isActive && (
                                <span className="absolute inset-y-0 left-0 flex items-center">
                                  <CheckIcon className="h-5 w-5" />
                                </span>
                              )}
                              <span className="relative">{category.title}</span>
                              {Boolean(category.articles?.length) && (
                                <span
                                  className={`relative ml-3 inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                    category.isActive
                                      ? "bg-background-primary text-text-on-primary"
                                      : "bg-background-gray text-text-dark-gray"
                                  }`}
                                >
                                  {category.articles.length}
                                </span>
                              )}
                            </LinkWrapper>
                          </Listbox.Option>
                        ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </>
            )}
          </Listbox>
          <div className="mt-2 flex rounded-md shadow-sm">
            <div className="relative flex flex-grow items-stretch focus-within:z-10">
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === "Enter") {
                    applySearchFilter(e);
                  }
                }}
                placeholder={
                  data.searchPlaceholder ? data.searchPlaceholder : "Search"
                }
                className="focus:ring-ring-pirmary block w-full rounded-none rounded-l-md border-border-gray focus:border-border-primary sm:text-sm"
              />
            </div>
            <button
              onClick={applySearchFilter}
              type="button"
              className="relative -ml-px inline-flex items-center rounded-r-md border border-border-gray bg-background-off-white px-4 py-2 text-sm font-medium text-text-dark-gray hover:bg-background-light-gray"
            >
              {data.searchButtonIcon ? (
                <>
                  <span className="sr-only">
                    {data.searchButtonText ? data.searchButtonText : "Search"}
                  </span>
                  <MediaWrapper
                    media={data.searchButtonIcon}
                    divClassName="w-4 h-4"
                  />
                </>
              ) : (
                <>
                  <span className="sr-only">
                    {data.searchButtonText ? data.searchButtonText : "Search"}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    className="h-4 w-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>

        <div className="hidden justify-between border-b border-border-gray md:flex">
          <div className="flex space-x-8 overflow-hidden">
            {Boolean(activeCategories.length) && (
              <div className="flex space-x-8">
                {activeCategories.map((category, index) => (
                  <LinkWrapper
                    key={index}
                    to={blogData.rootUrl}
                    className="flex whitespace-nowrap border-b-2 border-border-primary px-1 py-4 text-sm font-medium text-text-primary"
                  >
                    {category.title}
                    {category.articles?.length && (
                      <span className="ml-3 inline-block rounded-full bg-background-primary px-2.5 py-0.5 text-xs font-medium text-text-on-primary">
                        {category.articles.length}
                      </span>
                    )}
                  </LinkWrapper>
                ))}
              </div>
            )}
            <div className="flex space-x-8 overflow-hidden">
              {Boolean(inactiveCategories?.length) &&
                inactiveCategories.map((category, index) => (
                  <div
                    key={index}
                    ref={category.observer?.ref}
                    className={`${category.observer.inView ? "" : "opacity-0"}`}
                  >
                    <LinkWrapper
                      to={category.observer.inView ? category.url : null}
                      className="flex whitespace-nowrap border-b-2 border-border-transparent px-1 py-4 text-sm font-medium text-text-gray hover:border-border-dark-gray hover:text-text-dark-gray"
                    >
                      {category.title}
                      {category.articles?.length && (
                        <span className="ml-3 inline-block rounded-full bg-background-gray px-2.5 py-0.5 text-xs font-medium text-text-dark-gray">
                          {category.articles.length}
                        </span>
                      )}
                    </LinkWrapper>
                  </div>
                ))}
            </div>
          </div>
          <div className="flex">
            <div
              className={`mr-4 ${
                inactiveCategories.filter(
                  (category) => category.observer.inView
                ).length === inactiveCategories.length
                  ? "hidden"
                  : ""
              }`}
            >
              <Listbox value={null} onChange={() => {}}>
                {({ open }) => (
                  <>
                    <Listbox.Button
                      className={`flex whitespace-nowrap border-b-2 border-border-transparent px-1 py-4 text-sm font-medium text-text-gray  hover:border-border-dark-gray hover:text-text-dark-gray ${
                        open
                          ? "border-border-dark-gray text-text-dark-gray"
                          : ""
                      }`}
                    >
                      <span>
                        {data.buttonTextMore ? data.buttonTextMore : "More"}
                      </span>
                    </Listbox.Button>

                    <Transition
                      show={open}
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-60 overflow-auto rounded-md bg-background-white py-1 text-base shadow-lg ring-1 ring-ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {Boolean(inactiveCategories?.length) &&
                          inactiveCategories
                            .filter((category) => !category.observer.inView)
                            .map((category, index) => (
                              <Listbox.Option
                                key={index}
                                className="cursor-default select-none px-4 py-2 text-text-gray"
                              >
                                <LinkWrapper
                                  to={category.url}
                                  className="flex truncate font-normal hover:text-text-dark-gray"
                                >
                                  {category.title && (
                                    <span>{category.title}</span>
                                  )}
                                  {category.articles?.length && (
                                    <span className="relative ml-3 inline-block rounded-full bg-background-gray px-2.5 py-0.5 text-xs font-medium text-text-dark-gray">
                                      {category.articles.length}
                                    </span>
                                  )}
                                </LinkWrapper>
                              </Listbox.Option>
                            ))}
                      </Listbox.Options>
                    </Transition>
                  </>
                )}
              </Listbox>
            </div>
            <div className="my-auto flex w-64 flex-none rounded-md py-2 shadow-sm">
              <div className="relative flex flex-grow items-stretch focus-within:z-10">
                <input
                  type="text"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      applySearchFilter(e);
                    }
                  }}
                  placeholder={
                    data.searchPlaceholder ? data.searchPlaceholder : "Search"
                  }
                  className="focus:ring-ring-pirmary block w-full rounded-none rounded-l-md border-border-gray focus:border-border-primary sm:text-sm"
                />
              </div>
              <button
                onClick={applySearchFilter}
                type="button"
                className="relative -ml-px inline-flex items-center rounded-r-md border border-border-gray bg-background-off-white px-4 py-2 text-sm font-medium text-text-dark-gray hover:bg-background-light-gray"
              >
                {data.searchButtonIcon ? (
                  <>
                    <span className="sr-only">
                      {data.searchButtonText ? data.searchButtonText : "Search"}
                    </span>
                    <MediaWrapper
                      media={data.searchButtonIcon}
                      divClassName="w-4 h-4"
                    />
                  </>
                ) : (
                  <>
                    <span className="sr-only">
                      {data.searchButtonText ? data.searchButtonText : "Search"}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      className="h-4 w-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {posts?.length ? (
        <div className="mx-auto mt-8 grid max-w-lg gap-5 lg:max-w-none lg:grid-cols-3">
          {posts.map((article, index) => (
            <div
              key={index}
              className="flex flex-col overflow-hidden rounded-lg bg-background-white shadow-lg hover:bg-background-light-gray"
            >
              {article.headImage && (
                <LinkWrapper to={article.url} className="flex-shrink-0">
                  <MediaWrapper
                    media={article.headImage}
                    divClassName="w-full aspect-[2/1]"
                    className="h-full w-full"
                  />
                </LinkWrapper>
              )}
              <div className="flex h-full flex-col justify-between bg-background-transparent p-4">
                <div className="flex flex-shrink-0 flex-col">
                  {article.categories &&
                    article.categories.length &&
                    article.categories[0].title && (
                      <div className="flex">
                        <LinkWrapper
                          to={article.categories[0].url}
                          className="rounded-md px-2 hover:underline"
                        >
                          <div className="block">
                            <MarkdownWrapper
                              text={article.categories[0].title}
                              markdownClassName="text-sm font-medium text-text-secondary"
                            />
                          </div>
                        </LinkWrapper>
                      </div>
                    )}
                  {(article.title ||
                    article.descriptionShort ||
                    article.description) && (
                    <LinkWrapper
                      to={article.url}
                      className="mt-2 rounded-md px-2 hover:underline"
                    >
                      {article.title && (
                        <MarkdownWrapper
                          text={article.title}
                          markdownClassName="custom-font text-xl font-semibold text-text-primary"
                        />
                      )}
                      {(article.descriptionShort || article.description) && (
                        <MarkdownWrapper
                          text={
                            article.descriptionShort ||
                            limitTextByWordCount(
                              article.description,
                              descriptionMaxWordCount
                            )
                          }
                          markdownClassName="mt-3 text-base text-text-secondary"
                          replaceParagraph={false}
                        />
                      )}
                    </LinkWrapper>
                  )}
                </div>
                <div className="mt-6 flex items-center">
                  {article.author?.name && (
                    <div className="flex-shrink-0">
                      <LinkWrapper to={article.author.url}>
                        <span className="sr-only">{article.author.name}</span>
                        {article.author.image && (
                          <MediaWrapper
                            media={article.author.image}
                            divClassName="h-10 w-10"
                            className="rounded-full"
                          />
                        )}
                      </LinkWrapper>
                    </div>
                  )}
                  <div className="ml-3">
                    {article.author?.name && (
                      <div className="flex">
                        <LinkWrapper
                          to={article.author.url}
                          className="-ml-2 rounded-md px-2 hover:underline"
                        >
                          <MarkdownWrapper
                            text={article.author.name}
                            markdownClassName="text-sm font-medium text-text-primary"
                          />
                        </LinkWrapper>
                      </div>
                    )}
                    <div className="flex space-x-1 text-sm text-text-secondary">
                      {article.date && article.locale && (
                        <time
                          dateTime={new Date(article.date).toLocaleDateString(
                            article.locale,
                            {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }
                          )}
                        >
                          {new Date(article.date).toLocaleDateString(
                            article.locale,
                            {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }
                          )}
                        </time>
                      )}
                      {data.readingTimeText && article.readingTimeMinutes && (
                        <span>
                          {article.date && article.locale && (
                            <span aria-hidden="true" className="px-1">
                              &middot;
                            </span>
                          )}
                          <span>{`${article.readingTimeMinutes} ${data.readingTimeText}`}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mx-auto mt-12 flex max-w-lg justify-center lg:max-w-none">
          {data.noPostsFoundImage ? (
            <>
              <span className="sr-only">
                {data.noPostsFoundText
                  ? data.noPostsFoundText
                  : "No Posts Found"}
              </span>
              <MediaWrapper media={data.noPostsFoundImage} />
            </>
          ) : (
            <span className="text-center text-lg font-bold">
              {data.noPostsFoundText ? data.noPostsFoundText : "No Posts Found"}
            </span>
          )}
        </div>
      )}
      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-between bg-white px-4">
          <div className="flex flex-1 items-center justify-between sm:hidden">
            <button
              onClick={decreasePage}
              className={`inline-flex items-center rounded-md border border-border-light-gray bg-background-white px-4 py-2 text-sm font-medium text-text-gray hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-dark-gray ${
                isFirstPage(currentPage) ? "cursor-default opacity-0" : ""
              }`}
              disabled={isFirstPage(currentPage)}
            >
              {data.paginationPrevious ? data.paginationPrevious : "Previous"}
            </button>
            <span className="px-4 py-2 text-sm font-medium text-text-gray">{`${
              data.paginationCurrentPageText
                ? data.paginationCurrentPageText
                : "Page"
            } ${currentPage}/${totalPages}`}</span>
            <button
              onClick={increasePage}
              className={`ml-3 inline-flex items-center rounded-md border border-border-light-gray bg-background-white px-4 py-2 text-sm font-medium text-text-gray hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-dark-gray ${
                isLastPage(currentPage) ? "cursor-default opacity-0" : ""
              }`}
              disabled={isLastPage(currentPage)}
            >
              {data.paginationNext ? data.paginationNext : "Next"}
            </button>
          </div>
          <div className="hidden items-center justify-center sm:flex sm:flex-1">
            <div
              className="inline-flex bg-background-white text-center text-sm font-medium text-text-primary shadow-sm"
              aria-label="Pagination"
            >
              <button
                onClick={decreasePage}
                className={`inline-flex items-center justify-center rounded-l-md border border-border-light-gray px-2 py-2 ${
                  isFirstPage(currentPage)
                    ? "cursor-default text-text-light-gray"
                    : "hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                }`}
                disabled={isFirstPage(currentPage)}
              >
                <span className="sr-only">
                  {data.paginationPrevious
                    ? data.paginationPrevious
                    : "Previous"}
                </span>
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
              </button>
              {Boolean(pageLinksLeft?.length) && (
                <>
                  {pageLinksLeft.map((pageLink, index) => (
                    <button
                      key={index}
                      onClick={() => goToPage(pageLink)}
                      aria-current="page"
                      className="inline-flex w-10 items-center justify-center border border-border-light-gray px-4 py-2 hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                    >
                      {pageLink}
                    </button>
                  ))}
                  <span className="inline-flex w-10 items-center justify-center border border-border-light-gray px-4 py-2">
                    ...
                  </span>
                </>
              )}
              {Boolean(pageLinksMiddle?.length) &&
                pageLinksMiddle.map((pageLink, index) => (
                  <button
                    key={index}
                    onClick={() => goToPage(pageLink)}
                    aria-current="page"
                    className={`inline-flex w-10 items-center justify-center px-4 py-2 ${
                      isPageActive(pageLink)
                        ? "z-10 cursor-default bg-background-primary text-text-on-primary-light"
                        : "border border-border-light-gray hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                    }`}
                    disabled={isPageActive(pageLink)}
                  >
                    {pageLink}
                  </button>
                ))}
              {Boolean(pageLinksRight?.length) && (
                <>
                  <span className="inline-flex w-10 items-center justify-center border border-border-light-gray px-4 py-2">
                    ...
                  </span>
                  {pageLinksRight.map((pageLink, index) => (
                    <button
                      key={index}
                      onClick={() => goToPage(pageLink)}
                      aria-current="page"
                      className="inline-flex w-10 items-center justify-center border border-border-light-gray px-4 py-2 hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                    >
                      {pageLink}
                    </button>
                  ))}
                </>
              )}
              <button
                onClick={increasePage}
                className={`inline-flex items-center justify-center rounded-r-md border border-border-light-gray px-2 py-2 ${
                  isLastPage(currentPage)
                    ? "cursor-default text-text-light-gray"
                    : "hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                }`}
                disabled={isLastPage(currentPage)}
              >
                <span className="sr-only">
                  {data.paginationNext ? data.paginationNext : "Next"}
                </span>
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

ThreeColumnCardsWithFilter.propTypes = {
  data: PropTypes.object,
  blogData: PropTypes.object,
  articles: PropTypes.array,
  categories: PropTypes.array,
};

export default ThreeColumnCardsWithFilter;
